import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { ConversationsStore } from '../../../data/conversations.store';
import type { ListEventsParams } from '../../../data/conversations-api.service';
import { EventRendererHostComponent } from './event-renderer-host';
import { HISTORY_VISIBLE_EVENT_TYPES } from './event-registry.providers';
import { HistoryFiltersComponent, type HistoryFiltersValue } from './history-filters';
import { UiButton, UiEmptyState, UiSkeletonGrid } from '../../../../../shared/ui';

/**
 * Timeline tab of the conversation details panel. Coordinates the filter bar with the store's
 * `historyByConversation` slice — filter changes reload the page, scrollEnd loads the next page.
 */
@Component({
  selector: 'app-history-tab',
  imports: [EventRendererHostComponent, HistoryFiltersComponent, UiButton, UiEmptyState, UiSkeletonGrid],
  templateUrl: './history-tab.html',
  styleUrl: './history-tab.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistoryTabComponent {
  private readonly store = inject(ConversationsStore);

  readonly conversationId = input.required<string>();

  protected readonly filters = signal<HistoryFiltersValue>({});
  protected readonly history = this.store.selectedHistory;
  protected readonly events = computed(() => this.history().items);
  protected readonly loading = computed(() => this.history().loading);
  protected readonly hasMore = computed(() => this.history().hasMore);

  constructor() {
    effect(() => {
      const conversationId = this.conversationId();
      const filters = this.filters();
      untracked(() => this.store.loadHistory(conversationId, toListParams(filters)));
    });
  }

  protected applyFilters(value: HistoryFiltersValue): void {
    this.filters.set(value);
  }

  protected loadMore(): void {
    this.store.loadMoreHistory(this.conversationId());
  }
}

function toListParams(value: HistoryFiltersValue): ListEventsParams {
  // Only event types the registry knows how to render; intersect the user's selection
  // (when present) with the visible whitelist so the API never returns message.* / note.*
  // rows that the operational view intentionally excludes.
  const eventTypes = value.eventTypes?.length
    ? value.eventTypes.filter((eventType) => HISTORY_VISIBLE_EVENT_TYPES.includes(eventType))
    : HISTORY_VISIBLE_EVENT_TYPES;
  const params: ListEventsParams = { eventTypes };
  if (value.actorTypes?.length) params.actorTypes = value.actorTypes;
  if (value.sessionId) params.sessionId = value.sessionId;
  return params;
}
